// src/routes/BlogScreen.js
import React, { useState, useEffect } from 'react';
import BlogCard from '../components/Blog/BlogCard';
import BlogPost from '../components/Blog/BlogPost';
import BlogEditor from '../components/Blog/BlogEditor';
import {
  loadBlogPosts,
  updateBlogPost,
  addBlogPost,
  deleteBlogPost,
  isAdminUser
} from '../services/blogService';

// Add CSS for blog content
import '../App.css';

const BlogScreen = ({ darkMode }) => {
  const [posts, setPosts] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const blogPosts = await loadBlogPosts();
      setPosts(blogPosts);
      setIsLoading(false);
    };

    fetchPosts();
    setIsAdmin(isAdminUser());
  }, []);

  const handleReadMore = (postId) => {
    setSelectedPostId(postId);
  };

  const handleBackToList = () => {
    setSelectedPostId(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setIsEditing(true);
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setIsEditing(true);
  };

  const handleSavePost = async (post) => {
    setIsLoading(true);
    let updatedPosts;

    if (post.id) {
      // Update existing post
      updatedPosts = await updateBlogPost(post);
    } else {
      // Add new post
      updatedPosts = await addBlogPost(post);
    }

    if (updatedPosts) {
      setPosts(updatedPosts);
      setIsEditing(false);
      setEditingPost(null);

      // If we were editing a post that was being viewed, update the selected post ID
      if (selectedPostId === post.id) {
        setSelectedPostId(post.id);
      }
    }

    setIsLoading(false);
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту статью?')) {
      setIsLoading(true);
      const updatedPosts = await deleteBlogPost(postId);

      if (updatedPosts) {
        setPosts(updatedPosts);
        setSelectedPostId(null);
      }

      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingPost(null);
  };

  const selectedPost = posts.find(post => post.id === selectedPostId);

  if (isLoading) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <i className="fas fa-spinner fa-spin text-4xl mb-3"></i>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <BlogEditor
        post={editingPost}
        darkMode={darkMode}
        onSave={handleSavePost}
        onCancel={handleCancelEdit}
      />
    );
  }

  if (selectedPost) {
    return (
      <BlogPost
        post={selectedPost}
        darkMode={darkMode}
        onBack={handleBackToList}
        onEdit={handleEdit}
        onDelete={handleDeletePost}
        isAdmin={isAdmin}
      />
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`}>Блог</h2>
        {isAdmin && (
          <button
            onClick={handleNewPost}
            className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
          >
            <i className="fas fa-plus mr-1"></i> Новая статья
          </button>
        )}
      </div>

      {posts.length > 0 ? (
        posts.map(post => (
          <BlogCard
            key={post.id}
            post={post}
            darkMode={darkMode}
            onReadMore={handleReadMore}
          />
        ))
      ) : (
        <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <i className="fas fa-newspaper text-4xl mb-3"></i>
          <p>Статьи пока не добавлены</p>
        </div>
      )}
    </>
  );
};

export default BlogScreen;